import { Container, Heading, Text, VStack } from '@chakra-ui/react';
import { Formik } from 'formik';
import { useEffect, useRef } from 'react';

import { LEAccordion, LEAccordionItem } from '../../../../../components/LEAccordion';
import { useLEAccordionContext } from '../../../../../components/LEAccordion/useLEAccordion';
import { ACCORDION_NAME } from '../../../../../components/LEAccordion/utils';
import MarketplaceExplainerVideo from '../../../../../components/marketplace/MarketplaceExplainerVideo';
import { LDFlags } from '../../../../../constants/flags';
import { useFlag, useOnceWhen } from '../../../../../hooks';
import useMarketplacePayoffEligibility from '../../../../../hooks/useMarketplacePayoffEligibility';
import {
  trackConditionReportCompleted,
  trackConditionReportStarted,
} from '../../../../../services/analytics/marketplaceEvents';
import { UserDeal } from '../../../utils';
import MarketplaceErrorState from '../MarketplaceErrorState';
import { isStepComplete, stepsCompleteCount } from '../conditionReport/encodeDecodeAnswers';
import useMarketplaceConditionReport from '../conditionReport/useMarketplaceConditionReport';
import { isDocumentSatisfied, useDocumentUpload } from '../live/postAcceptance/useDocumentUpload';
import { buildPhotoSlotMap } from '../photos/buildPhotoSlotMap';
import { PRELIST_ODOMETER_SLOT_ID, PRELIST_PHOTO_SLOT_ID } from '../photos/constants';
import useMarketplaceDashboardState from '../useMarketplaceDashboardState';
import MarketplacePrelistConditionStepContent from './MarketplacePrelistConditionStepContent';
import MarketplacePrelistLoadingSkeleton from './MarketplacePrelistLoadingSkeleton';
import MarketplacePrelistPayoffItem from './MarketplacePrelistPayoffItem';
import MarketplacePrelistPhotoItem from './MarketplacePrelistPhotoItem';
import MarketplacePrelistVerifyItem from './MarketplacePrelistVerifyItem';

const OpenItemOnLoad = ({ targetIndex }: { targetIndex: number }) => {
  const { itemStates, openAccordionIndex, setOpenAccordionIndex } = useLEAccordionContext();
  const settled = useRef(false);

  useEffect(() => {
    if (settled.current) return;
    const registered = Object.values(itemStates).some((item) => item.index === targetIndex);
    if (!registered) return;
    if (openAccordionIndex === targetIndex) {
      settled.current = true;
      return;
    }
    setOpenAccordionIndex(targetIndex);
  }, [itemStates, openAccordionIndex, targetIndex, setOpenAccordionIndex]);

  return null;
};

type MarketplacePrelistDashboardProps = {
  userDeal: UserDeal;
};

const MarketplacePrelistDashboard = ({ userDeal }: MarketplacePrelistDashboardProps) => {
  const dealId = userDeal?.id || '';
  const customerId = userDeal?.customer?.id || '';
  const {
    error,
    isLoading: stateLoading,
    refetch,
    photos,
    lead,
  } = useMarketplaceDashboardState(dealId);
  const hasExistingPayoff = (userDeal?.car?.payoff?.vehicle_payoff ?? 0) > 0;
  const payoffEligibility = useMarketplacePayoffEligibility(
    dealId,
    false,
    hasExistingPayoff,
    userDeal?.car?.payoff?.lienholder_slug,
  );
  const form = useMarketplaceConditionReport(dealId);
  const phoneVerificationEnabled = useFlag(LDFlags.MARKETPLACE_PHONE_VERIFICATION);
  const photosAndIdVariant = useFlag(LDFlags.MARKETPLACE_PRELIST_PHOTOS_AND_ID);
  const explainerPlacement = useFlag(LDFlags.MARKETPLACE_EXPLAINER_VIDEO_PLACEMENT);
  const { statusByType: documentStatusByType, statusReady: documentStatusReady } =
    useDocumentUpload(dealId, { enabled: photosAndIdVariant });
  const verifiedOnLoadRef = useRef<boolean | null>(null);

  useOnceWhen(form.isReady, () => trackConditionReportStarted(dealId));
  useOnceWhen(
    form.isReady &&
      form.steps.length > 0 &&
      stepsCompleteCount(form.steps, form.initialValues) >= form.steps.length,
    () => trackConditionReportCompleted(dealId),
  );

  if (error) {
    return (
      <Container maxW="container.xl" my="20px" px={{ base: '16px', md: '24px' }}>
        <MarketplaceErrorState onRetry={refetch} />
      </Container>
    );
  }

  if (
    !userDeal ||
    stateLoading ||
    !form.isReady ||
    payoffEligibility.loading ||
    (photosAndIdVariant && !documentStatusReady)
  ) {
    return <MarketplacePrelistLoadingSkeleton />;
  }

  const showPayoff = payoffEligibility.shouldRender;
  const photoSlotMap = buildPhotoSlotMap(photos);
  const licenseSatisfied = isDocumentSatisfied(documentStatusByType.drivers_license);
  const photosComplete = photosAndIdVariant
    ? photoSlotMap.has(PRELIST_PHOTO_SLOT_ID) &&
      photoSlotMap.has(PRELIST_ODOMETER_SLOT_ID) &&
      licenseSatisfied
    : photoSlotMap.has(PRELIST_PHOTO_SLOT_ID);

  if (verifiedOnLoadRef.current === null) {
    verifiedOnLoadRef.current = lead?.smsConsent?.status === 'verified';
  }
  const showVerifyItem = phoneVerificationEnabled && !verifiedOnLoadRef.current;

  const verifyOffset = showVerifyItem ? 1 : 0;

  const photoStepIndex = form.steps.length + verifyOffset;
  const payoffStepIndex = form.steps.length + 1 + verifyOffset;

  const firstIncompleteConditionStep = form.steps.findIndex(
    (step) => !isStepComplete(step, form.initialValues),
  );

  let initialOpenIndex = photoStepIndex;
  if (showVerifyItem) {
    initialOpenIndex = 0;
  } else if (firstIncompleteConditionStep !== -1) {
    initialOpenIndex = firstIncompleteConditionStep;
  } else if (!photosComplete) {
    initialOpenIndex = photoStepIndex;
  } else if (showPayoff) {
    initialOpenIndex = payoffStepIndex;
  }

  return (
    <Container maxW="container.xl" my="20px" px={{ base: '16px', md: '24px' }}>
      <VStack spacing={2} align="center" textAlign="center" mb={6}>
        <Heading
          as="h1"
          fontSize={{ base: '24px', md: '30px' }}
          fontWeight="800"
          color="grayDark"
          lineHeight="1.2"
        >
          Set up your listing
        </Heading>
        <Text fontSize={{ base: '14px', md: '16px' }} color="grayLight" maxW="600px">
          A few details, then we confirm your payoff and your car goes in front of buyers.
        </Text>
      </VStack>

      {(explainerPlacement === 'condition_report' || explainerPlacement === 'both') && dealId && (
        <MarketplaceExplainerVideo placement="condition_report" dealId={dealId} />
      )}
      <Formik
        initialValues={form.initialValues}
        validationSchema={form.validationSchema}
        enableReinitialize
        onSubmit={() => undefined}
        validateOnChange={false}
        validateOnBlur
      >
        <LEAccordion name={ACCORDION_NAME.MARKETPLACE_PRELIST_ACCORDION} showProgress={false}>
          <OpenItemOnLoad targetIndex={initialOpenIndex} />
          {showVerifyItem && (
            <MarketplacePrelistVerifyItem dealId={dealId} customerId={customerId} />
          )}
          {form.steps.map((step) => (
            <LEAccordionItem
              key={step.title}
              accordionKey={step.title}
              title={step.stepLabel.toUpperCase()}
              initialStatus={isStepComplete(step, form.initialValues) ? 'complete' : 'none'}
            >
              <MarketplacePrelistConditionStepContent
                step={step}
                isSaving={form.isSaving}
                saveStep={form.saveStep}
              />
            </LEAccordionItem>
          ))}
          <MarketplacePrelistPhotoItem
            dealId={dealId}
            isLast={!showPayoff}
            initialStatus={photosComplete ? 'complete' : 'none'}
            photosAndIdVariant={photosAndIdVariant}
          />
          {showPayoff && (
            <MarketplacePrelistPayoffItem
              dealId={dealId}
              missingFields={payoffEligibility.missingFields}
              requiredFields={payoffEligibility.requiredFields}
              lienholderName={userDeal?.car?.payoff?.lienholder_name}
              lienholderSlug={userDeal?.car?.payoff?.lienholder_slug}
              retailBookValue={userDeal?.car?.retail_book_value}
            />
          )}
        </LEAccordion>
      </Formik>
    </Container>
  );
};

export default MarketplacePrelistDashboard;
